import { AlertTriangle, Database, Hand, RefreshCw, Shield } from 'lucide-react';
import { type LucideIcon } from 'lucide-react';

import { formatarDataHora, formatarRelativo } from '@/nucleo/formato/datas';
import { cn } from '@/nucleo/utilitarios';
import { Badge } from '@/ui/base/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';

import { EXPLICACAO_DA_ORIGEM, ROTULO_DA_ORIGEM, type OrigemDoDado } from './contrato';

/**
 * De onde o registro veio, e quando foi a ultima vez que conferiu com a origem.
 *
 * POR QUE UMA COLUNA SO PARA ISSO. Titular, condutor, monitor e veiculo do TEG vem, em
 * regra, do SGTP. Quando a integracao cai, a operacao nao para: cadastra-se em
 * contingencia e reconcilia depois. O efeito colateral e que a mesma tabela mistura
 * dado conferido com a origem e dado digitado as pressas — e quem decide algo sobre
 * uma linha precisa saber em qual dos dois casos esta.
 *
 * "há 2 minutos" NO LUGAR DA DATA ABSOLUTA, com a data exata no tooltip. A pergunta
 * real e "isto esta fresco?", e `29/05/2026 14:32` obriga quem le a fazer a subtracao
 * de cabeca, linha por linha. A data absoluta continua alcancavel porque, na hora de
 * justificar uma decisao, e ela que vai no parecer.
 *
 * O AVISO DE CONTINGENCIA E DIFERENTE DA ORIGEM, e os dois aparecem juntos de
 * proposito: `origem_dado` diz como o registro NASCEU, `em_contingencia` diz se ele
 * esta AGORA fora de sincronia. Registro criado em contingencia e ja reconciliado tem
 * a primeira marca e nao a segunda.
 *
 * SOMENTE LEITURA, E ISSO E DELIBERADO. No protótipo esta celula abria um painel
 * "Sincronizar agora", com comparacao lado a lado entre o dado atual e o do SGTP. A
 * integracao com o SGTP nao existe neste sistema ainda, e um botao que dispara nada
 * e pior que a ausencia dele — o operador clica, nao ve efeito e conclui que a
 * integracao esta quebrada. O que o usuario nao pode fazer nao e renderizado
 * (CLAUDE.md, "Regras de interface", item 7). Quando o modulo de integracao entrar, a
 * acao volta para ca.
 */

interface ApresentacaoDaOrigem {
  icone: LucideIcon;
  variante: 'secondary' | 'outline' | 'default';
}

const APRESENTACAO: Record<OrigemDoDado, ApresentacaoDaOrigem> = {
  INTEGRACAO_SGTP: { icone: Database, variante: 'secondary' },
  MANUAL_CONTINGENCIA: { icone: Hand, variante: 'outline' },
  MANUAL_ASSISTIDO: { icone: Shield, variante: 'default' },
  SISTEMA_TEG: { icone: RefreshCw, variante: 'outline' },
};

function ehOrigemConhecida(valor: string | null | undefined): valor is OrigemDoDado {
  return !!valor && valor in APRESENTACAO;
}

interface UltimaSincronizacaoCellProps {
  origem: string | null | undefined;
  /** ISO 8601 em UTC, ou nulo quando nunca sincronizou. */
  sincronizadoEm: string | null | undefined;
  emContingencia?: boolean | null;
  className?: string;
}

export function UltimaSincronizacaoCell({
  origem,
  sincronizadoEm,
  emContingencia,
  className,
}: UltimaSincronizacaoCellProps) {
  const conhecida = ehOrigemConhecida(origem);

  /**
   * Sem origem E sem data: o registro nunca passou por integracao nenhuma. Dizer
   * "Sem sincronização" e mais honesto que mostrar um selo vazio — nao ha o que
   * detalhar, e o tooltip nao teria conteudo.
   */
  if (!conhecida && !sincronizadoEm) {
    return (
      <span className={cn('text-muted-foreground text-xs italic', className)}>
        Sem sincronização
      </span>
    );
  }

  const apresentacao = conhecida ? APRESENTACAO[origem] : null;
  const Icone = apresentacao?.icone ?? Hand;
  const rotulo = conhecida ? ROTULO_DA_ORIGEM[origem] : 'Origem não informada';
  const explicacao = conhecida
    ? EXPLICACAO_DA_ORIGEM[origem]
    : 'O registro não declara de onde veio.';

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('flex cursor-help flex-col items-start gap-1', className)}>
            <Badge variant={apresentacao?.variante ?? 'outline'} className="gap-1">
              <Icone className="h-3 w-3" aria-hidden="true" />
              {rotulo}
              {emContingencia && (
                <AlertTriangle
                  className="text-warning h-3 w-3"
                  aria-label="Em contingência"
                  role="img"
                />
              )}
            </Badge>
            <span className="text-muted-foreground whitespace-nowrap text-[11px]">
              {sincronizadoEm ? formatarRelativo(sincronizadoEm) : 'sem registro'}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1 text-xs">
            <p className="font-semibold">{rotulo}</p>
            <p className="text-muted-foreground">{explicacao}</p>
            <p className="text-muted-foreground">
              {sincronizadoEm
                ? `Última sincronização em ${formatarDataHora(sincronizadoEm)}.`
                : 'Nunca sincronizou com a origem.'}
            </p>
            {emContingencia && (
              <p className="text-warning font-medium">
                Fora de sincronia — precisa reconciliar quando a integração voltar.
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
